import { useState, useCallback, useContext } from "react";
import { PublicKey, TransactionInstruction, SystemProgram, Keypair } from "@solana/web3.js";
import {
    getAssociatedTokenAddress,
    createAssociatedTokenAccountIdempotentInstruction,
    getAccount,
    TOKEN_PROGRAM_ID,
} from "@solana/spl-token";
import { LAMPORTS_PER_SOL } from "@solana/web3.js";
import { useAnchorWallet, useConnection, useWallet } from "@solana/wallet-adapter-react";
import { AppConfigContext } from "../context";
import { DripVaultDescriptor } from "../constants/dripVaults";
import { deriveDripPdas } from "../features/drip/dripPdas";
import { dripProgramFor, fetchDripOracleAccounts } from "../features/drip/dripFetch";
import * as dripIdl from "../idl/drip_vault_v2.json";
import {
    createDripDepositPayload,
    createDripUpdateRewardTokenPayload,
    createDripDepositAssetPayload,
    createDripUpdateRewardTokenAssetPayload,
    depositSolPayload,
    updateVaultBalancePayload,
    createMintToPayload,
} from "../helpers/payloads";
import { sendTx } from "./utils/utils";
import { getWalletErrorMessage } from "./utils/walletErrors";
import { resolveMintedAmount } from "./utils/resolveMintedAmount";
import { fetchJitoStakePoolAccounts } from "./utils/jitoStakePool";
import mixpanel from "mixpanel-browser";
import { useJitoSOLRate } from "./useJitoSOLRate";

// The Drip program has two instruction families (legacy kySOL vs. generic
// asset vaults) whose encoders are byte-identical in signature — only the
// discriminator differs. That means a call site can invoke the wrong
// encoder for a vault's kind, compile, and silently target the wrong
// on-chain instruction. Route every deposit / reward-token encoding through
// these two functions so the kind -> discriminator mapping lives in exactly
// one place.
export const dripDepositData = (
    vault: DripVaultDescriptor,
    amount: number,
    rewardTokenMint: PublicKey | null
): Buffer =>
    vault.kind === "asset"
        ? createDripDepositAssetPayload(amount, rewardTokenMint)
        : createDripDepositPayload(amount, rewardTokenMint);

export const dripUpdateRewardTokenData = (
    vault: DripVaultDescriptor,
    newRewardTokenMint: PublicKey
): Buffer =>
    vault.kind === "asset"
        ? createDripUpdateRewardTokenAssetPayload(newRewardTokenMint)
        : createDripUpdateRewardTokenPayload(newRewardTokenMint);

export const useDripDeposit = (vault: DripVaultDescriptor) => {
    const { connection } = useConnection();
    const { publicKey, sendTransaction } = useWallet();
    const wallet = useAnchorWallet();
    const { 
        environment, 
        priorityFeeLevel, 
        dripProgramAddress,
        jitoSolMintAddress,
        solVrtMintAddress,
        vaultProgramAddress,
        vaultConfigAddress,
        solVaultAddress,
        kyrosFeeWalletAddress
    } = useContext(AppConfigContext);
    const { fetchConversionRate } = useJitoSOLRate();

    const dripProgram = new PublicKey(dripProgramAddress);
    const jitoSolMint = new PublicKey(jitoSolMintAddress);
    const kySolMint = new PublicKey(solVrtMintAddress);
    const vaultProgram = new PublicKey(vaultProgramAddress);
    const vaultConfig = new PublicKey(vaultConfigAddress);
    // The legacy vault-program account that mints kySOL from JitoSOL. Named
    // distinctly from the `vault: DripVaultDescriptor` parameter above; the
    // two are unrelated on-chain accounts and this hook only ever touches
    // this one on the kysol path.
    const solVaultAccount = new PublicKey(solVaultAddress);
    const feeWallet = new PublicKey(kyrosFeeWalletAddress);
    const signer = publicKey;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentStep, setCurrentStep] = useState<'idle' | 'converting' | 'depositing'>('idle');

    const convertSolToKySol = useCallback(async (amount: string) => {
        setError(null);

        if (!signer || !wallet) {
            throw new Error("Please connect your wallet.");
        }

        if (amount === '' || Number(amount) === 0) {
            throw new Error("Please enter an amount greater than 0.");
        }

        if (environment === "devnet") {
            throw new Error("SOL to kySOL conversion is not available on devnet.");
        }

        setLoading(true);
        setCurrentStep('converting');

        let ixs: TransactionInstruction[] = [];
        const amountInLamports = Math.round(Number(amount) * LAMPORTS_PER_SOL);
        const temporaryAccount = Keypair.generate();

        try {
            // ========================================
            // TX 1: SOL → JitoSOL → kySOL
            // ========================================

            // Snapshot kySOL balance before conversion
            const userKySolAtaBefore = await getAssociatedTokenAddress(kySolMint, signer, true);
            let kySolBalanceBefore = BigInt(0);
            try {
                const accountBefore = await getAccount(connection, userKySolAtaBefore);
                kySolBalanceBefore = accountBefore.amount;
            } catch {
                // ATA doesn't exist yet, balance is 0
            }

            // Fetch Jito stake pool accounts dynamically from on-chain state
            const jitoAccounts = await fetchJitoStakePoolAccounts(connection);
            
            // Get conversion rate for SOL to JitoSOL
            const jitoAmountToMint = await fetchConversionRate(connection, amount);
            if (!jitoAmountToMint) {
                throw new Error("Couldn't fetch the JitoSOL conversion rate. Please try again.");
            }

            const jitoAmountOutLamports = Math.floor(Number(jitoAmountToMint) * LAMPORTS_PER_SOL);
            const jitoAmountOutLamportsSlippage = Math.floor(jitoAmountOutLamports * 0.95);

            // 1.1 Transfer SOL to temporary account
            const transferToTempIx = SystemProgram.transfer({
                fromPubkey: signer,
                toPubkey: temporaryAccount.publicKey,
                lamports: amountInLamports
            });
            ixs.push(transferToTempIx);

            // 1.2 Get/Create ATA for user's JitoSOL
            const userJitoSolAta = await getAssociatedTokenAddress(
                jitoSolMint,
                signer,
                true
            );

            const createIdempotentUserJitoSolAtaIx = createAssociatedTokenAccountIdempotentInstruction(
                signer,
                userJitoSolAta,
                signer,
                jitoSolMint
            );
            ixs.push(createIdempotentUserJitoSolAtaIx);

            // 1.3 Deposit SOL to get JitoSOL
            const depositSolIx = new TransactionInstruction({
                keys: [
                    { pubkey: jitoAccounts.jitoStakePool, isSigner: false, isWritable: true },
                    { pubkey: jitoAccounts.stakePoolWithdrawAuthority, isSigner: false, isWritable: false },
                    { pubkey: jitoAccounts.reserveStakeAccount, isSigner: false, isWritable: true },
                    { pubkey: temporaryAccount.publicKey, isSigner: true, isWritable: true },
                    { pubkey: userJitoSolAta, isSigner: false, isWritable: true },
                    { pubkey: jitoAccounts.stakeFeeAccount, isSigner: false, isWritable: true },
                    { pubkey: userJitoSolAta, isSigner: false, isWritable: true },
                    { pubkey: jitoSolMint, isSigner: false, isWritable: true },
                    { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
                    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
                ],
                programId: jitoAccounts.stakePoolProgram,
                data: depositSolPayload(amountInLamports)
            });
            ixs.push(depositSolIx);

            // 2.1 Get/Create ATA for user's kySOL
            const userKySolAta = await getAssociatedTokenAddress(
                kySolMint,
                signer,
                true
            );

            const createIdempotentUserKySolAtaIx = createAssociatedTokenAccountIdempotentInstruction(
                signer,
                userKySolAta,
                signer,
                kySolMint
            );
            ixs.push(createIdempotentUserKySolAtaIx);

            // 2.2 Get/Create ATA for vault's JitoSOL
            const vaultJitoSolAta = await getAssociatedTokenAddress(
                jitoSolMint,
                solVaultAccount,
                true
            );

            const createIdempotentVaultJitoSolAtaIx = createAssociatedTokenAccountIdempotentInstruction(
                signer,
                vaultJitoSolAta,
                solVaultAccount,
                jitoSolMint
            );
            ixs.push(createIdempotentVaultJitoSolAtaIx);

            // 2.3 Get/Create ATA for fee wallet's kySOL
            const feeWalletKySolAta = await getAssociatedTokenAddress(
                kySolMint,
                feeWallet,
                true
            );

            const createIdempotentFeeWalletKySolAtaIx = createAssociatedTokenAccountIdempotentInstruction(
                signer,
                feeWalletKySolAta,
                feeWallet,
                kySolMint
            );
            ixs.push(createIdempotentFeeWalletKySolAtaIx);

            // 2.4 Update vault balance
            const updateVaultBalanceIx = new TransactionInstruction({
                keys: [
                    { pubkey: vaultConfig, isSigner: false, isWritable: false },
                    { pubkey: solVaultAccount, isSigner: false, isWritable: true },
                    { pubkey: vaultJitoSolAta, isSigner: false, isWritable: true },
                    { pubkey: kySolMint, isSigner: false, isWritable: true },
                    { pubkey: feeWalletKySolAta, isSigner: false, isWritable: true },
                    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
                ],
                programId: vaultProgram,
                data: updateVaultBalancePayload()
            });
            ixs.push(updateVaultBalanceIx);

            // 2.5 Mint kySOL from JitoSOL
            const mintToIx = new TransactionInstruction({
                keys: [
                    { pubkey: vaultConfig, isSigner: false, isWritable: false },
                    { pubkey: solVaultAccount, isSigner: false, isWritable: true },
                    { pubkey: kySolMint, isSigner: false, isWritable: true },
                    { pubkey: signer, isSigner: true, isWritable: true },
                    { pubkey: userJitoSolAta, isSigner: false, isWritable: true },
                    { pubkey: vaultJitoSolAta, isSigner: false, isWritable: true },
                    { pubkey: userKySolAta, isSigner: false, isWritable: true },
                    { pubkey: feeWalletKySolAta, isSigner: false, isWritable: true },
                    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
                    { pubkey: vaultProgram, isSigner: false, isWritable: true },
                ],
                programId: vaultProgram,
                data: createMintToPayload(jitoAmountOutLamports, jitoAmountOutLamportsSlippage)
            });
            ixs.push(mintToIx);

            const success = await sendTx(environment, connection, signer, sendTransaction, ixs, priorityFeeLevel, [temporaryAccount]);

            if (success) {
                // The conversion succeeded, so kySOL WAS minted. Poll until the
                // credit is visible on the RPC node (it can lag right after
                // confirmation) and return the delta; never return 0, which would
                // wrongly abort the drip deposit and orphan the freshly-minted kySOL.
                return await resolveMintedAmount({
                    balanceBefore: kySolBalanceBefore,
                    fetchBalance: async () => (await getAccount(connection, userKySolAtaBefore)).amount,
                    estimate: jitoAmountOutLamports,
                });
            }
            return null;
        } catch (err) {
            setError(getWalletErrorMessage(err, "Converting SOL to kySOL failed. Please try again."));
            throw err;
        } finally {
            setLoading(false);
            setCurrentStep('idle');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signer, wallet]);

    const convertJitoSolAndDeposit = useCallback(async (
        amount: string,
        rewardTokenMint?: string
    ) => {
        // Every account below is a legacy kySOL account (unseeded PDAs, the
        // kySOL mint, the kySOL-minting vault program), while the instruction
        // data comes from the kind-branching dripDepositData. Only deposit()'s
        // early return keeps asset vaults out today; state the invariant here
        // so a future call path fails loudly instead of building a mismatched
        // instruction.
        if (vault.kind !== 'kysol') {
            throw new Error(
                `convertJitoSolAndDeposit builds legacy kySOL instructions only; vault "${vault.id}" is kind "${vault.kind}".`
            );
        }

        setError(null);

        if (!signer || !wallet) {
            throw new Error("Please connect your wallet.");
        }

        if (amount === '' || Number(amount) === 0) {
            throw new Error("Please enter an amount greater than 0.");
        }

        const rewardTokenMintPubkey = rewardTokenMint ? new PublicKey(rewardTokenMint) : null;

        setLoading(true);
        setCurrentStep('depositing');

        let ixs: TransactionInstruction[] = [];
        const amountInLamports = Math.round(Number(amount) * LAMPORTS_PER_SOL);

        try {
            // ========================================
            // Single TX: JitoSOL → kySOL → Drip
            // ========================================

            // Get user's JitoSOL ATA
            const userJitoSolAta = await getAssociatedTokenAddress(
                jitoSolMint,
                signer,
                true
            );

            // Get/Create ATA for user's kySOL
            const userKySolAta = await getAssociatedTokenAddress(
                kySolMint,
                signer,
                true
            );

            const createIdempotentUserKySolAtaIx = createAssociatedTokenAccountIdempotentInstruction(
                signer,
                userKySolAta,
                signer,
                kySolMint
            );
            ixs.push(createIdempotentUserKySolAtaIx);

            // Get/Create ATA for vault's JitoSOL
            const vaultJitoSolAta = await getAssociatedTokenAddress(
                jitoSolMint,
                solVaultAccount,
                true
            );

            const createIdempotentVaultJitoSolAtaIx = createAssociatedTokenAccountIdempotentInstruction(
                signer,
                vaultJitoSolAta,
                solVaultAccount,
                jitoSolMint
            );
            ixs.push(createIdempotentVaultJitoSolAtaIx);

            // Get/Create ATA for fee wallet's kySOL
            const feeWalletKySolAta = await getAssociatedTokenAddress(
                kySolMint,
                feeWallet,
                true
            );

            const createIdempotentFeeWalletKySolAtaIx = createAssociatedTokenAccountIdempotentInstruction(
                signer,
                feeWalletKySolAta,
                feeWallet,
                kySolMint
            );
            ixs.push(createIdempotentFeeWalletKySolAtaIx);

            // Step 1: Update vault balance
            const updateVaultBalanceIx = new TransactionInstruction({
                keys: [
                    { pubkey: vaultConfig, isSigner: false, isWritable: false },
                    { pubkey: solVaultAccount, isSigner: false, isWritable: true },
                    { pubkey: vaultJitoSolAta, isSigner: false, isWritable: true },
                    { pubkey: kySolMint, isSigner: false, isWritable: true },
                    { pubkey: feeWalletKySolAta, isSigner: false, isWritable: true },
                    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
                ],
                programId: vaultProgram,
                data: updateVaultBalancePayload()
            });
            ixs.push(updateVaultBalanceIx);

            // Step 2: Mint kySOL from JitoSOL
            const jitoAmountOutLamportsSlippage = Math.floor(amountInLamports * 0.95);
            const mintToIx = new TransactionInstruction({
                keys: [
                    { pubkey: vaultConfig, isSigner: false, isWritable: false },
                    { pubkey: solVaultAccount, isSigner: false, isWritable: true },
                    { pubkey: kySolMint, isSigner: false, isWritable: true },
                    { pubkey: signer, isSigner: true, isWritable: true },
                    { pubkey: userJitoSolAta, isSigner: false, isWritable: true },
                    { pubkey: vaultJitoSolAta, isSigner: false, isWritable: true },
                    { pubkey: userKySolAta, isSigner: false, isWritable: true },
                    { pubkey: feeWalletKySolAta, isSigner: false, isWritable: true },
                    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
                    { pubkey: vaultProgram, isSigner: false, isWritable: true },
                ],
                programId: vaultProgram,
                data: createMintToPayload(amountInLamports, jitoAmountOutLamportsSlippage)
            });
            ixs.push(mintToIx);

            // Step 3: Derive drip PDAs
            const [vaultState] = PublicKey.findProgramAddressSync(
                [Buffer.from("vault_state")],
                dripProgram
            );

            const [userPosition] = PublicKey.findProgramAddressSync(
                [Buffer.from("user_position"), signer.toBuffer()],
                dripProgram
            );

            const [kySolVault] = PublicKey.findProgramAddressSync(
                [Buffer.from("ky_sol_vault")],
                dripProgram
            );

            const [shareMint] = PublicKey.findProgramAddressSync(
                [Buffer.from("share_mint")],
                dripProgram
            );

            // Get user's share token account (ATA)
            const userShareAccount = await getAssociatedTokenAddress(
                shareMint,
                signer
            );

            const createIdempotentUserShareAccountIx = createAssociatedTokenAccountIdempotentInstruction(
                signer,
                userShareAccount,
                signer,
                shareMint
            );
            ixs.push(createIdempotentUserShareAccountIx);

            const oracleAccounts = await fetchDripOracleAccounts(
                dripProgramFor(dripIdl, dripProgramAddress, connection),
                vault,
                dripProgram
            );

            // Step 4: Deposit kySOL to drip
            const dripDepositIx = new TransactionInstruction({
                keys: [
                    { pubkey: vaultState, isSigner: false, isWritable: true },
                    { pubkey: userPosition, isSigner: false, isWritable: true },
                    { pubkey: kySolVault, isSigner: false, isWritable: true },
                    { pubkey: userKySolAta, isSigner: false, isWritable: true },
                    { pubkey: userShareAccount, isSigner: false, isWritable: true },
                    { pubkey: shareMint, isSigner: false, isWritable: true },
                    { pubkey: kySolMint, isSigner: false, isWritable: false },
                    { pubkey: signer, isSigner: true, isWritable: true },
                    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
                    { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
                    // deposit settles accrued yield before pricing this deposit, so the
                    // vault's rate oracle is required: 2 accounts for kySOL, 1 for asset
                    // vaults. See fetchDripOracleAccounts.
                    ...oracleAccounts,
                ],
                programId: dripProgram,
                data: dripDepositData(vault, amountInLamports, rewardTokenMintPubkey)
            });
            ixs.push(dripDepositIx);

            return await sendTx(environment, connection, signer, sendTransaction, ixs, priorityFeeLevel);
        } catch (err) {
            setError(getWalletErrorMessage(err, "The JitoSOL deposit failed. Please try again."));
            throw err;
        } finally {
            setLoading(false);
            setCurrentStep('idle');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signer, wallet, vault.kind, vault.id]);

    const depositToVault = useCallback(async (
        kySolAmount: number,
        rewardTokenMint?: string
    ) => {
        // Legacy kySOL accounts only — see convertJitoSolAndDeposit. Asset
        // vaults go through depositAssetToVault.
        if (vault.kind !== 'kysol') {
            throw new Error(
                `depositToVault builds legacy kySOL instructions only; vault "${vault.id}" is kind "${vault.kind}".`
            );
        }

        setError(null);

        if (!signer || !wallet) {
            throw new Error("Please connect your wallet.");
        }

        const rewardTokenMintPubkey = rewardTokenMint ? new PublicKey(rewardTokenMint) : null;

        setLoading(true);
        setCurrentStep('depositing');

        let ixs: TransactionInstruction[] = [];

        try {
            // ========================================
            // TX 2: kySOL → Drip Deposit
            // ========================================

            // Get user's kySOL ATA
            const userKySolAta = await getAssociatedTokenAddress(
                kySolMint,
                signer,
                true
            );

            // Derive PDA for drip vault_state
            const [vaultState] = PublicKey.findProgramAddressSync(
                [Buffer.from("vault_state")],
                dripProgram
            );

            // Derive PDA for user_position
            const [userPosition] = PublicKey.findProgramAddressSync(
                [Buffer.from("user_position"), signer.toBuffer()],
                dripProgram
            );

            // Derive PDA for kySOL vault in drip program
            const [kySolVault] = PublicKey.findProgramAddressSync(
                [Buffer.from("ky_sol_vault")],
                dripProgram
            );

            // Derive PDA for share_mint
            const [shareMint] = PublicKey.findProgramAddressSync(
                [Buffer.from("share_mint")],
                dripProgram
            );

            // Get user's share token account (ATA)
            const userShareAccount = await getAssociatedTokenAddress(
                shareMint,
                signer
            );

            // Create idempotent instruction for user's share account
            const createIdempotentUserShareAccountIx = createAssociatedTokenAccountIdempotentInstruction(
                signer,
                userShareAccount,
                signer,
                shareMint
            );
            ixs.push(createIdempotentUserShareAccountIx);

            const oracleAccounts = await fetchDripOracleAccounts(
                dripProgramFor(dripIdl, dripProgramAddress, connection),
                vault,
                dripProgram
            );

            // Create drip deposit instruction
            const dripDepositIx = new TransactionInstruction({
                keys: [
                    { pubkey: vaultState, isSigner: false, isWritable: true },
                    { pubkey: userPosition, isSigner: false, isWritable: true },
                    { pubkey: kySolVault, isSigner: false, isWritable: true },
                    { pubkey: userKySolAta, isSigner: false, isWritable: true },
                    { pubkey: userShareAccount, isSigner: false, isWritable: true },
                    { pubkey: shareMint, isSigner: false, isWritable: true },
                    { pubkey: kySolMint, isSigner: false, isWritable: false },
                    { pubkey: signer, isSigner: true, isWritable: true },
                    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
                    { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
                    // deposit settles accrued yield before pricing this deposit, so the
                    // vault's rate oracle is required: 2 accounts for kySOL, 1 for asset
                    // vaults. See fetchDripOracleAccounts.
                    ...oracleAccounts,
                ],
                programId: dripProgram,
                data: dripDepositData(vault, kySolAmount, rewardTokenMintPubkey)
            });
            ixs.push(dripDepositIx);

            return await sendTx(environment, connection, signer, sendTransaction, ixs, priorityFeeLevel);
        } catch (err) {
            setError(getWalletErrorMessage(err, "The deposit to Drip failed. Please try again."));
            throw err;
        } finally {
            setLoading(false);
            setCurrentStep('idle');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signer, wallet, vault.kind, vault.id]);

    // Deposit an LST straight into its asset vault. One instruction; no
    // conversion hop, which is why hSOL only accepts hSOL.
    const depositAssetToVault = useCallback(async (
        amountRaw: number,
        rewardTokenMint?: string
    ) => {
        setError(null);

        if (!signer || !wallet) {
            throw new Error("Please connect your wallet.");
        }

        const rewardTokenMintPubkey = rewardTokenMint ? new PublicKey(rewardTokenMint) : null;

        setLoading(true);
        setCurrentStep('depositing');

        const ixs: TransactionInstruction[] = [];

        try {
            const depositMint = new PublicKey(vault.depositMint);
            const { vaultState, userPosition, reserve, shareMint } = deriveDripPdas(
                vault,
                dripProgram,
                signer
            );

            if (!userPosition) {
                throw new Error("Please connect your wallet.");
            }

            const userTokenAccount = await getAssociatedTokenAddress(
                depositMint,
                signer,
                true
            );

            const userShareAccount = await getAssociatedTokenAddress(
                shareMint,
                signer
            );

            // The program requires user_share_account to exist already.
            ixs.push(createAssociatedTokenAccountIdempotentInstruction(
                signer,
                userShareAccount,
                signer,
                shareMint
            ));

            const oracleAccounts = await fetchDripOracleAccounts(
                dripProgramFor(dripIdl, dripProgramAddress, connection),
                vault,
                dripProgram
            );

            ixs.push(new TransactionInstruction({
                keys: [
                    { pubkey: vaultState, isSigner: false, isWritable: true },
                    { pubkey: userPosition, isSigner: false, isWritable: true },
                    { pubkey: reserve, isSigner: false, isWritable: true },
                    { pubkey: userTokenAccount, isSigner: false, isWritable: true },
                    { pubkey: userShareAccount, isSigner: false, isWritable: true },
                    { pubkey: shareMint, isSigner: false, isWritable: true },
                    { pubkey: depositMint, isSigner: false, isWritable: false },
                    { pubkey: signer, isSigner: true, isWritable: true },
                    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
                    { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
                    // deposit settles accrued yield before pricing this deposit, so the
                    // vault's rate oracle is required: 2 accounts for kySOL, 1 for asset
                    // vaults. See fetchDripOracleAccounts.
                    ...oracleAccounts,
                ],
                programId: dripProgram,
                data: dripDepositData(vault, amountRaw, rewardTokenMintPubkey),
            }));

            return await sendTx(environment, connection, signer, sendTransaction, ixs, priorityFeeLevel);
        } catch (err) {
            setError(getWalletErrorMessage(err, `The ${vault.symbol} deposit failed. Please try again.`));
            throw err;
        } finally {
            setLoading(false);
            setCurrentStep('idle');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signer, wallet, vault.id, vault.kind, vault.depositMint, vault.symbol]);

    const deposit = useCallback(async (
        amount: string,
        inputToken: string,
        rewardTokenMint?: string
    ) => {
        setError(null);

        if (!signer || !wallet) {
            throw new Error("Please connect your wallet.");
        }

        mixpanel.track('Drip Deposit Started', {
            'amount': amount,
            'input_token': inputToken,
            'vault': vault.id,
            'wallet': wallet.publicKey?.toBase58(),
            'reward_token': rewardTokenMint || 'none',
        });

        // Asset vaults take their LST directly; the kySOL conversion paths below
        // are unreachable for them.
        if (vault.kind === 'asset') {
            try {
                const amountRaw = Math.round(Number(amount) * LAMPORTS_PER_SOL);
                const success = await depositAssetToVault(amountRaw, rewardTokenMint);

                if (success) {
                    mixpanel.track('Drip Deposit Completed', {
                        'amount': amount,
                        'input_token': inputToken,
                        'vault': vault.id,
                        'wallet': wallet.publicKey?.toBase58(),
                        'reward_token': rewardTokenMint || 'none',
                    });
                }
                return success;
            } catch (err) {
                setError(getWalletErrorMessage(err, "Something went wrong with the deposit. Please try again."));
                throw err;
            }
        }

        try {
            // Route based on input token
            if (inputToken === 'kySOL') {
                // Direct deposit: kySOL → Drip (1 transaction)
                const amountInLamports = Math.round(Number(amount) * LAMPORTS_PER_SOL);
                const success = await depositToVault(amountInLamports, rewardTokenMint);
                
                if (success) {
                    mixpanel.track('Drip Deposit Completed', {
                        'amount': amount,
                        'input_token': inputToken,
                        'vault': vault.id,
                        'wallet': wallet.publicKey?.toBase58(),
                        'reward_token': rewardTokenMint || 'none',
                    });
                }
                return success;
            } else if (inputToken === 'JitoSOL') {
                // Batched: JitoSOL → kySOL → Drip (1 transaction)
                const success = await convertJitoSolAndDeposit(amount, rewardTokenMint);
                
                if (success) {
                    mixpanel.track('Drip Deposit Completed', {
                        'amount': amount,
                        'input_token': inputToken,
                        'vault': vault.id,
                        'wallet': wallet.publicKey?.toBase58(),
                        'reward_token': rewardTokenMint || 'none',
                    });
                }
                return success;
            } else {
                // SOL → JitoSOL → kySOL → Drip (2 transactions)
                const kySolAmount = await convertSolToKySol(amount);
                if (!kySolAmount) {
                    throw new Error("The SOL to kySOL conversion produced no kySOL. Please try again.");
                }

                // Deposit kySOL to drip
                const success = await depositToVault(kySolAmount, rewardTokenMint);
                
                if (success) {
                    mixpanel.track('Drip Deposit Completed', {
                        'amount': amount,
                        'input_token': inputToken,
                        'vault': vault.id,
                        'wallet': wallet.publicKey?.toBase58(),
                        'reward_token': rewardTokenMint || 'none',
                    });
                }

                return success;
            }
        } catch (err) {
            setError(getWalletErrorMessage(err, "Something went wrong with the deposit. Please try again."));
            throw err;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signer, wallet, vault.kind, vault.id, convertSolToKySol, convertJitoSolAndDeposit, depositToVault, depositAssetToVault]);

    const updateRewardToken = useCallback(async (newRewardTokenMint: string) => {
        setError(null);

        if (!signer || !wallet) {
            throw new Error("Please connect your wallet.");
        }

        setLoading(true);
        setCurrentStep("depositing");

        try {
            const newRewardMintPubkey = new PublicKey(newRewardTokenMint);
            const rewardTokenData = dripUpdateRewardTokenData(vault, newRewardMintPubkey);

            const { vaultState, userPosition } = deriveDripPdas(vault, dripProgram, signer);

            if (!userPosition) {
                throw new Error("Please connect your wallet.");
            }

            const updateRewardTokenIx = vault.kind === 'asset'
                ? new TransactionInstruction({
                    keys: [
                        { pubkey: vaultState, isSigner: false, isWritable: false },
                        { pubkey: userPosition, isSigner: false, isWritable: true },
                        { pubkey: new PublicKey(vault.depositMint), isSigner: false, isWritable: false },
                        // signer is read-only for update_reward_token_asset.
                        { pubkey: signer, isSigner: true, isWritable: false },
                    ],
                    programId: dripProgram,
                    data: rewardTokenData,
                })
                : new TransactionInstruction({
                    keys: [
                        { pubkey: userPosition, isSigner: false, isWritable: true },
                        { pubkey: signer, isSigner: true, isWritable: true },
                    ],
                    programId: dripProgram,
                    data: rewardTokenData,
                });

            const success = await sendTx(
                environment,
                connection,
                signer,
                sendTransaction,
                [updateRewardTokenIx],
                priorityFeeLevel
            );

            if (success) {
                mixpanel.track("Drip Reward Token Updated", {
                    vault: vault.id,
                    wallet: wallet.publicKey?.toBase58(),
                    reward_token: newRewardTokenMint,
                });
            }

            return success;
        } catch (err) {
            setError(getWalletErrorMessage(err, "The reward token update failed. Please try again."));
            throw err;
        } finally {
            setLoading(false);
            setCurrentStep("idle");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signer, wallet, vault.kind, vault.depositMint, vault.id]);

    return { deposit, updateRewardToken, loading, error, currentStep };
};
